// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { Dispatch } from 'redux';
import { getJson, postJson, deleteRequest, patchJson } from 'Utils/http';
import { API_URL } from 'Config';
import type { RootState } from 'Store';
import { SetFoldersAction, UpdateProjectThumbnailAction } from 'Features/folderList/folderListActions';
import { getAllFoldersThunk } from 'Features/folderList/folderListThunk';
import { GetMapCoordinatesFailedAction,
  MapCoordinatesFetchDroppedAction,
  InvalidateMapCoordinatesAction, GetMapCoordinatesStartAction, GetMapCoordinatesSuccessAction, GetProjectsFailedAction, GetProjectsStartAction, GetProjectsSuccessAction, SetKeywordFilterAction, SetLoadAllAction, SetLoadNewAction, SetPageAction, SetProjectsAction, SetSortFilterAction, SetThumbnailAction } from './projectListActions';
import { selectEffectiveOrderSortFilter } from './projectListSelector';
import { toRequestFilter } from './projectViewOptions';

// Gets projects for a given user
export const GetProjectsThunk = (orgId: string = '', limit: number = 9) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    const { page, keywordFilter, sortFilter, loadNew } = getState().projectList;
    const orderSortFilter = selectEffectiveOrderSortFilter(getState());
    const url = `${API_URL}/projects?page=${page}&limit=${limit}&query=${encodeURIComponent(keywordFilter)}&sort=${sortFilter}&orgId=${orgId}&filterby=${toRequestFilter(orderSortFilter) || ''}`;
    const { token } = getState().auth;

    dispatch(GetProjectsStartAction());

    const result = await getJson<IProjectResponse>(url, token);
    // Out-of-order guard against what THIS request was sent with, never the server's echo -
    // sanitization rewrites the echo, which made every response look stale and stranded the
    // loading flag over an emptied grid. The effective filter is part of the request, so it
    // is part of the guard - a slower older-filter response must not clobber the newer one.
    if (getState().projectList.keywordFilter !== keywordFilter
      || selectEffectiveOrderSortFilter(getState()) !== orderSortFilter) return {};

    if (result.success) {
      if (loadNew) {
        dispatch(SetProjectsAction([]));
        dispatch(SetFoldersAction([]));
      }
      dispatch(GetProjectsSuccessAction({
        list: result.data.list,
        totalCount: result.data.totalCount,
        folders: result.data.folders,
      }));

      // Update folder list
      const existingFolderIds = new Set(getState().folderList.folderList.map((folder: IFolder) => folder._id));
      const newFolders = result.data.folders.filter((folder: IFolder) => !existingFolderIds.has(folder._id));
      dispatch(SetFoldersAction([...getState().folderList.folderList, ...newFolders]));

      if (result.data.list.length === 0) {
        dispatch(SetLoadAllAction(true));
      }
    } else {
      dispatch(GetProjectsFailedAction(result.message));
    }

    dispatch(SetLoadNewAction(false));

    return result;
  };

// One source of truth for the coordinates cache key - the thunk stamps it on success and
// useDashboardProjects compares against it; hand-built copies drifting apart would silently
// break the cache in either direction. Keyed by what the server is asked, so a filter the
// client applies itself rides the set it narrows rather than fetching a copy of it.
export const buildMapCoordinatesKey = (keyword: string, filter: string) => `${keyword}|${toRequestFilter(filter) || ''}`;

// Gets the map coordinate of every project matching the current search/filter, independent of
// the paginated list the grid loads - map view plots all matching projects from this, and shows
// how many were excluded for having no resolvable location.
export const GetMapCoordinatesThunk = () =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    // The effective filter, not the raw one - the grid fetch widens an untouched default the
    // same way, and map and grid must plot the same project set for the same UI state.
    const requestKeyword = getState().projectList.keywordFilter;
    const requestFilter = selectEffectiveOrderSortFilter(getState());
    const requestKey = buildMapCoordinatesKey(requestKeyword, requestFilter);
    const url = `${API_URL}/projects/coordinates?query=${encodeURIComponent(requestKeyword)}&filterby=${toRequestFilter(requestFilter) || ''}`;
    const { token } = getState().auth;

    dispatch(GetMapCoordinatesStartAction());

    const result = await getJson<IGetProjectCoordinatesResponse>(url, token);

    // Drop out-of-order responses by comparing what THIS request was sent with against the
    // store now - never against the server's echo, which sanitization can rewrite (making
    // every response look stale and starving the loading flag forever).
    // Compared as the server was asked: a switch between two filters that send the same request
    // does not make the answer stale.
    const keywordNow = getState().projectList.keywordFilter;
    const filterNow = selectEffectiveOrderSortFilter(getState());
    if (requestKeyword !== keywordNow || toRequestFilter(requestFilter) !== toRequestFilter(filterNow)) {
      // Dropped as out of order. If no newer request is coming (the store already answers
      // the current key), the loading flag must not stay stranded.
      if (buildMapCoordinatesKey(keywordNow, filterNow) === getState().projectList.mapCoordinatesKey) {
        dispatch(MapCoordinatesFetchDroppedAction());
      }
      return result;
    }

    if (result.success) {
      dispatch(GetMapCoordinatesSuccessAction({
        coordinates: result.data.coordinates,
        requestKey,
      }));
    } else {
      dispatch(GetMapCoordinatesFailedAction(result.message));
    }

    return result;
  };

// Reload projects after a data mutation the dashboard cannot apply locally - a project created,
// say. Invalidating first so the cached set cannot answer for the stale one, then refetching
// the coordinates set the dashboard renders from. The folders are refetched too: a project
// created into a folder changes that folder's count, and the folder page fetches its projects
// only when the count says there are some.
export const ReloadProjectsThunk = () =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(InvalidateMapCoordinatesAction());
    dispatch(SetLoadNewAction(true));
    dispatch(SetLoadAllAction(false));
    dispatch(SetPageAction(0));
    dispatch(SetProjectsAction([]));
    await GetMapCoordinatesThunk()(dispatch, getState);
    await getAllFoldersThunk()(dispatch, getState);
  };

// Reset sort filter and search
export const ResetFiltersThunk = () =>
  async (dispatch: Dispatch) => {
    dispatch(SetSortFilterAction('updatedAtD'));
    dispatch(SetKeywordFilterAction(''));
  };

// Read thumbnail with a given projectId
// In-flight thumbnail resolutions, so the rail card and the pin card asking for the same
// project within a moment mint one signed URL, not two racing ones.
const inFlightThumbnails = new Set<string>();

export const ReadThumbnailThunk = (projectId: string) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    if (inFlightThumbnails.has(projectId)) return;
    inFlightThumbnails.add(projectId);
    const { token } = getState().auth;
    let orgId;
    if ('opsTrainer' in getState().auth.resources) {
      // Rail cards come from the unpaginated coordinates set, so a project need not be in
      // the paginated grid list - fall back to the coordinate entry's owning org.
      const project = getState().projectList?.list?.find((p) => p._id === projectId);
      const coordinate = getState().projectList?.mapCoordinates?.find((c) => c._id === projectId);
      orgId = project?.ownerOrganization || coordinate?.ownerOrganization;
    } else {
      orgId = getState().auth.user.ownerOrganization;
    }
    const url = `${API_URL}/readThumbnail/${orgId}/${projectId}`;
    try {
      const result = await getJson<string>(url, token);

      if (result.success) {
        const obj = { projectId, thumbnail: result.data };
        dispatch(SetThumbnailAction(obj));
        dispatch(UpdateProjectThumbnailAction(obj));
      }
    } finally {
      inFlightThumbnails.delete(projectId);
    }
  };
